/**
 * ENG-6236: single inbound-access decision point across all channel adapters.
 *
 * "Who is allowed to communicate with this agent?" used to be answered by
 * several independent gates that were AND-ed together, evaluated at different
 * points in each channel's Socket Mode / webhook handler, sourced from
 * different config, with inconsistent observability — and the channels had
 * drifted from one another (Slack carried a blanket `allowed_users` message
 * gate that silently `return`ed; Telegram silently `continue`d on
 * `ALLOWED_CHATS`; Teams had its own parallel tenant gate). ENG-6235 deleted
 * the worst offender (the Slack blanket gate) and established the doctrine
 * that `sender_policy` is the single conversation authority. This module
 * finishes the job: one typed decision function that every channel adapter
 * calls exactly once, with a single labelled outcome for every message.
 *
 * The composer does NOT re-implement the channel-specific leaves — the
 * content filter, sender-policy mode logic, and peer classifier are deeply
 * tied to each platform's event shape and are already pure and tested. What
 * it owns is the part that was scattered and drifting:
 *
 *   1. the PRECEDENCE (content → identity → command),
 *   2. the single OUTCOME shape (admit | command | drop), and
 *   3. the decline-vs-silent decision, driven by `sameOrg`.
 *
 * Each adapter computes its channel-specific leaf results, feeds them in, and
 * switches on the one returned decision. Parity is asserted by feeding
 * equivalent leaf inputs through this one function from every channel's tests
 * and getting the same decision shape back.
 *
 * Decline-vs-silent (ENG-6236, Brad 2026-06-10): a sender who is IN THE SAME
 * org/workspace/tenant as the agent but is not admitted must always receive a
 * polite decline ("no silent drops" for insiders). A sender from OUTSIDE the
 * org boundary is dropped silently — replying would leak the agent's
 * existence to an external party (the existence-leak posture the host-runtime
 * 404s already protect). The composer encodes this as: `decline` is populated
 * iff `sameOrg` is true AND the block is not `internal_only` (which is
 * structurally external — see step 4). The adapter delivers `decline` when
 * present and stays silent when it is not.
 */

import type { SenderPolicyBlockReason } from './sender-policy-decline.js';
import { politeDeclineCopy } from './sender-policy-decline.js';

/**
 * Labelled, exhaustive reason for every non-admit. Every drop carries one of
 * these so the uniform log line is greppable and no drop is anonymous. The
 * template-literal arms keep the channel-specific sub-reason (the
 * sender-policy axis, the peer telemetry reason) attached without flattening
 * them into an opaque string.
 */
export type InboundDropReason =
  // Content filter — channel noise (edits, deletes, empty, unresolvable
  // sender, wrong subtype/type). Not an access denial; always silent.
  | `content:${string}`
  // The agent's own message echoed back. Always silent.
  | 'self'
  // Channel-level org boundary (external Slack Connect workspace, foreign
  // Teams tenant, non-allowlisted Telegram chat). The sender is external by
  // definition, so this is always silent regardless of `sameOrg`.
  | 'org_boundary'
  // The CHAT ITSELF is a kind the agent is not permitted to participate in
  // (CS-1628: an agent configured for 1:1 use only, added to a group). Silent
  // for the same reason as `org_boundary`: replying "I don't do groups" in a
  // group the operator never sanctioned is itself the engagement being refused.
  | 'chat_type'
  // Human sender-policy mode gate (manager_only / team_only / agents_only /
  // …, or internal_only). Carries the existing decline-classifier reason.
  | `sender_policy:${SenderPolicyBlockReason}`
  // Peer/agent governance gate (mode off, unknown peer, cross-team grant
  // missing, not addressed, …). Carries the peer classifier's telemetry
  // reason. Peers are agents, never same-org humans, so always silent.
  | `peer:${string}`;

/** The one shape every channel adapter switches on. */
export type InboundAccessDecision =
  | { kind: 'admit' }
  | {
      kind: 'command';
      command: 'help' | 'restart' | 'investigate' | 'status' | 'watch' | 'onboard' | 'resume-onboarding' | 'ping';
      /**
       * Whether the sender is authorised to RUN the command. The adapter
       * dispatches the command when true and posts an explicit denial when
       * false — never a silent no-op (the whole point of routing commands
       * through the decision point: `/restart` from an unauthorised user gets
       * a visible "you're not allowed", not a confusing nothing).
       */
      authorized: boolean;
    }
  | {
      kind: 'drop';
      reason: InboundDropReason;
      /**
       * User-facing polite-decline copy. Present iff the sender is same-org
       * (see module header). Adapter posts it (rate-limited) when present and
       * stays silent when absent.
       */
      decline?: string;
    };

export interface InboundAccessInput {
  /**
   * Channel content filter result (Slack `decideSlackMessageForward`, Teams
   * `decideTeamsActivityForward`, Telegram's content check). When it declines,
   * the message is channel noise, not an access decision — dropped silently.
   */
  content: { forward: true } | { forward: false; reason: string };

  /** True when this is the agent's own message echoed back by the platform. */
  isSelf: boolean;

  /**
   * True when the sender is a bot/agent. When true the peer-governance layer
   * (`peer`) is applied IN ADDITION to the sender-policy axis — they are
   * additive layers, not an either/or. This mirrors the live Slack handler,
   * where `decideSenderPolicyForward` runs for every sender (humans and
   * label-bearing agents alike) BEFORE the peer classifier.
   */
  isBot: boolean;

  /**
   * Peer-classifier result for bot-authored messages (Slack
   * `classifyPeerMessage`, Teams `classifyTeamsPeerMessage`, Telegram
   * `classifyPeerMessage`). Consulted only when `isBot`. `kind:'human'` /
   * `kind:'peer-ingress'` fall through to the command/admit tail; `self` and
   * `drop` end the decision.
   *
   * ENG-8669: `kind:'bot-ingress'` is an allowlisted THIRD-PARTY bot (Linear,
   * CloudWatch, PagerDuty) waking the agent. It admits on the same path as a
   * peer, deliberately - the whole point is that the agent gets to act on it -
   * but it is a distinct kind because it is INGRESS-ONLY: see `isReplyEligible`
   * in slack-peer-classifier.ts. Never collapse the two into one kind; that
   * would make a third-party bot reply-eligible and reintroduce the bot-to-bot
   * chatter ENG-4936 exists to prevent.
   */
  peer?: { kind: 'human' | 'self' | 'drop' | 'peer-ingress' | 'bot-ingress'; reason?: string };

  /**
   * Channel-level org/tenant/chat membership boundary, applied to ALL senders
   * before the sender policy. Used by channels that express access purely as a
   * membership allowlist rather than a sender-policy mode (Telegram
   * `ALLOWED_CHATS`). Slack/Teams fold their org boundary (`internal_only` /
   * tenant) into `senderPolicy` instead and omit this. A `forward:false` here
   * means the sender is external — always a silent drop.
   */
  orgBoundary?: { forward: true } | { forward: false };

  /**
   * Sender-policy result + block classifier (Slack `decideSenderPolicyForward`
   * + `classifySlackPolicyBlock`, Teams equivalents). Applies to ALL senders
   * on channels that have a sender-policy mode system — a label-bearing peer
   * agent is gated here too, before the peer-governance layer. Present only on
   * channels that have a sender-policy mode system.
   */
  senderPolicy?: { forward: true } | { forward: false; reason: SenderPolicyBlockReason };
  /**
   * CS-1628 — whether this KIND of chat is one the agent may participate in at
   * all, independent of who sent the message. Telegram's "private chats only"
   * policy is the first user: an assistant that should only ever talk 1:1 with
   * its owner should not have to curate a list of group ids it has never seen
   * and cannot enumerate.
   *
   * Optional and absent-means-forward: every existing adapter omits it and
   * keeps today's behaviour exactly. A new axis that changed behaviour for
   * agents nobody configured would be a regression dressed as a feature.
   */
  chatType?: { forward: true } | { forward: false };

  /**
   * Slash-command detection + per-command authorization, evaluated by the
   * adapter (e.g. `isRestartSenderAllowed` for `/restart`, the non-empty
   * allowlist requirement for `/investigate`). Only reached once the identity
   * axes admit — a sender-policy-blocked user does not get to run commands,
   * matching the pre-consolidation order.
   */
  command?: {
    command: 'help' | 'restart' | 'investigate' | 'status' | 'watch' | 'onboard' | 'resume-onboarding' | 'ping';
    authorized: boolean;
  };

  /**
   * Whether the sender is in the same org/workspace/tenant as the agent.
   * Drives decline-vs-silent: same-org non-admits get a polite decline,
   * external ones stay silent (existence-leak posture). `help` is always
   * informational and unaffected.
   */
  sameOrg: boolean;
}

/**
 * ENG-10440: Slack same-workspace test for `InboundAccessInput.sameOrg`.
 *
 * The home workspace comes from the provisioner's `SLACK_HOME_TEAM_ID` first
 * and, failing that, from the bot's own `auth.test`. The fallback exists
 * because the env var was only ever emitted under `internal_only`, so for most
 * orgs `sameOrg` was false on every message and no mode-block decline could
 * ever be sent: robin dropped a same-workspace team member in total silence.
 *
 * Still fails CLOSED: with neither source known (the few seconds before
 * auth.test answers, or it never does) the sender is treated as external and
 * stays silent, so a Slack Connect outsider is never told the agent exists.
 */
export function isSameSlackWorkspace(input: {
  senderTeamId: string | undefined;
  provisionedHomeTeamId: string | undefined;
  authTestHomeTeamId: string | null;
}): boolean {
  const home =
    (input.provisionedHomeTeamId && input.provisionedHomeTeamId.length > 0
      ? input.provisionedHomeTeamId
      : null) ?? input.authTestHomeTeamId;
  return !!home && typeof input.senderTeamId === 'string' && input.senderTeamId === home;
}

/**
 * The single inbound-access decision. Precedence, fixed and shared across
 * every channel adapter:
 *
 *   1. content filter   — silent drop on channel noise (edits, empties, …)
 *   2. self             — silent drop on the agent's own echo
 *   3. org boundary     — silent drop for senders outside the membership
 *                         allowlist (external workspace / tenant / chat)
 *   4. sender policy    — mode + internal_only gate, applied to ALL senders;
 *                         same-org block → polite decline, external → silent
 *   5. command          — a recognised slash command, once the sender cleared
 *                         the conversation gates (matches every channel today,
 *                         which intercepts commands before the peer classifier)
 *   6. peer governance  — additional layer for bot/agent senders
 *   7. admit
 *
 * The only place an inbound message's fate is decided. No adapter may add a
 * user-gating `return` outside this function.
 */
export function decideInboundAccess(input: InboundAccessInput): InboundAccessDecision {
  // 1. Content filter — channel noise. Never a same-org decline; not an
  //    access denial.
  if (!input.content.forward) {
    return { kind: 'drop', reason: `content:${input.content.reason}` };
  }

  // 2. Own echo.
  if (input.isSelf) {
    return { kind: 'drop', reason: 'self' };
  }

  // 3. Channel membership boundary (e.g. Telegram ALLOWED_CHATS). External by
  //    definition — silent (existence-leak posture).
  if (input.orgBoundary && !input.orgBoundary.forward) {
    return { kind: 'drop', reason: 'org_boundary' };
  }

  // 3b. Chat-kind boundary (CS-1628). Sits with the org boundary rather than
  //     with sender policy because it is a fact about the ROOM, not the
  //     sender: no principal is permitted here, so evaluating who sent it
  //     would be answering a question that cannot change the outcome. Silent,
  //     and deliberately BEFORE the command interception below — a `/restart`
  //     from a group must not execute in a chat the agent may not be in.
  if (input.chatType && !input.chatType.forward) {
    return { kind: 'drop', reason: 'chat_type' };
  }

  // 4. Sender policy — applies to every sender (humans and label-bearing
  //    agents). Same-org senders are always told why; external senders stay
  //    silent.
  //
  //    `internal_only` is structurally external — it fires only when the
  //    sender is outside the home workspace/tenant, OR when the home id is
  //    unconfigured and the filter fails closed (drops everyone). In BOTH
  //    cases a decline would risk leaking the agent's existence to an outside
  //    party, so we suppress it here unconditionally rather than trusting each
  //    adapter to compute `sameOrg` correctly for the fail-closed case (a
  //    misconfigured `internal_only` would otherwise default `sameOrg` to true
  //    and decline to outsiders). Mode blocks (manager_only / team_only / …)
  //    are only reachable once the sender has cleared the workspace boundary,
  //    so they decline on `sameOrg` as normal.
  if (input.senderPolicy && !input.senderPolicy.forward) {
    const reason = input.senderPolicy.reason;
    const declineWorthy = input.sameOrg && reason !== 'internal_only';
    return {
      kind: 'drop',
      reason: `sender_policy:${reason}`,
      decline: declineWorthy ? politeDeclineCopy(reason) : undefined,
    };
  }

  // 5. Command — a recognised slash command from a HUMAN sender who cleared
  //    the conversation gates, intercepted before peer governance. Commands
  //    are operator actions: a bot-authored message that merely contains
  //    command syntax must NOT trigger one — otherwise peer/bot traffic
  //    bypasses peer governance here (a bot `/restart` could queue a restart
  //    whenever the allowlist is empty; a bot `/help` would get a reply).
  //    Bots fall through to peer governance below. Adapters should also avoid
  //    building a command for bot senders; this `!isBot` guard is the
  //    structural backstop that guarantees it for every channel (CodeRabbit).
  if (input.command && !input.isBot) {
    return {
      kind: 'command',
      command: input.command.command,
      authorized: input.command.authorized,
    };
  }

  // 6. Peer governance — additional gate for bot/agent senders, on top of the
  //    sender policy above.
  if (input.isBot) {
    const peer = input.peer;
    // Defensive: a bot message with no peer classification can't be reasoned
    // about — fail closed (silent; peers are never same-org humans).
    if (!peer || peer.kind === 'self') {
      return { kind: 'drop', reason: peer?.kind === 'self' ? 'self' : 'peer:unclassified' };
    }
    if (peer.kind === 'drop') {
      return { kind: 'drop', reason: `peer:${peer.reason ?? 'unspecified'}` };
    }
    // 'human' (not actually a managed peer), 'peer-ingress' (an admitted peer
    // turn) and 'bot-ingress' (ENG-8669: an allowlisted third-party bot waking
    // us) all fall through to admit. The three differ in what the agent may do
    // NEXT, not in whether the message arrives - and that difference is carried
    // by the classification kind, not re-derived here.
  }

  // 7. Admit.
  return { kind: 'admit' };
}
